import { useCallback } from 'react';

import { Currencies, MoneyType } from '../../../../types';

import {
  FETCH_CACHE_LAST_DEFAULT_CURRENCY_RATE_QUERY,
  FetchCacheLastDefaultCurrencyRateQueryResponse
} from '../../queries/fetchCacheLastDefaultCurrencyRate.query';

import { useCacheLastDefaultCurrencyRate } from '../useCacheLastDefaultCurrencyRate';

import { CurrencyRateCache } from '../../CurrencyRateCache';

import { amountMultiply } from '../../../../utils/amountMultiply';
import { amountDivide } from '../../../../utils/amountDivide';

function useConvertCurrencyRates() {
  const {
    lastDefaultCurrencyRate,
    lastDefaultCurrencyRateLoading,
    lastDefaultCurrencyRateError
  } = useCacheLastDefaultCurrencyRate<FetchCacheLastDefaultCurrencyRateQueryResponse>(
    {
      cacheKey: CurrencyRateCache.lastDefaultCacheKey(),
      query: FETCH_CACHE_LAST_DEFAULT_CURRENCY_RATE_QUERY
    }
  );

  const item = lastDefaultCurrencyRate;

  const convertCurrencyFromUsd = useCallback(
    (value: MoneyType, toCurrency: Currencies) =>
      amountMultiply(value, item?.data?.[toCurrency] || 1),
    [item?.data]
  );

  const convertCurrencyToUsd = useCallback(
    (value: MoneyType, fromCurrency: Currencies) =>
      amountDivide(value, item?.data?.[fromCurrency] || 1),
    [item?.data]
  );

  const convertCurrencyCrossExchangeUsd = useCallback(
    (value: MoneyType, fromCurrency: Currencies, toCurrency: Currencies) => {
      if (fromCurrency === toCurrency) return value;

      return convertCurrencyFromUsd(
        convertCurrencyToUsd(value, fromCurrency),
        toCurrency
      );
    },
    [convertCurrencyFromUsd, convertCurrencyToUsd]
  );

  return {
    currencyRatesError: lastDefaultCurrencyRateError,
    currencyRatesLoading: lastDefaultCurrencyRateLoading,
    currencyRatesId: item?.id,
    currencyRatesData: item?.data,
    convertCurrencyFromUsd,
    convertCurrencyToUsd,
    convertCurrencyCrossExchangeUsd
  };
}

export default useConvertCurrencyRates;
